import { updateNotificationCenterFilters } from 'modules/notificationCenter/actions';
import { notificationCenterSelector } from 'modules/notificationCenter/selectors';
import { Filters, NotificationStatus } from 'modules/notificationCenter/types';
import { createOrderByOptions, createStatusOptions } from 'modules/notificationCenter/utils';
import { SyntheticEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { LargeSegmentedControl } from 'ui/segmentedControl';
import { ALIGNMENT, BasicSelect } from 'ui/selects';
import { spacing } from 'ui/theme';
import { ORDER_BY_OPTIONS } from '../../constants';
import { MarkAllReadButton } from './MarkAllReadButton';

export const StyledMenu = styled.div`
  display: flex;
  gap: ${spacing.half};
  margin-bottom: ${spacing.quarter};
  justify-content: space-between;
`;

const ReadUnreadContainer = styled.div`
  display: flex;
  gap: ${spacing.half};
  align-items: center;
`;

export const NotificationActions = () => {
  const dispatch = useDispatch();
  const { filters, isLoading, notificationCount } = useSelector(notificationCenterSelector);
  const { status } = filters;
  const statusOptions = createStatusOptions(isLoading, notificationCount?.totalUnread);
  const orderByOptions = createOrderByOptions();

  /**
   * Expects a Partial<Filters> key/value pair to update
   * Dispatches a filter update which in turn dispatches a new getNotifications request
   */
  const handleFilterUpdate = (filter: Partial<Filters>) => {
    const newFilters = { ...filters, ...filter };
    dispatch(updateNotificationCenterFilters(newFilters, true));
  };

  /**
   * Captures a click event in the BasicSelect and stops event propagation
   * from closing the open Notification Menu when the orderBy filter is selected
   */
  const handleStopPropagation = (e: SyntheticEvent) => e.stopPropagation();

  const hasUnreadNotifications = notificationCount?.totalUnread > 0;

  return (
    <StyledMenu id="menu">
      <ReadUnreadContainer>
        <LargeSegmentedControl
          data-test="notification-status"
          onSelect={(value) => handleFilterUpdate({ status: value, sortDesc: true })}
          options={statusOptions}
          value={status}
        />
        {status === NotificationStatus.UNREAD && hasUnreadNotifications && <MarkAllReadButton />}
      </ReadUnreadContainer>
      <div onClick={handleStopPropagation}>
        <BasicSelect
          data-test="notification-sortby"
          id="select"
          isDisabled={!!isLoading}
          onSelect={([value]) => {
            handleFilterUpdate({
              orderBy: `${value}`,
              sortDesc: value !== ORDER_BY_OPTIONS.Level.filterName,
            });
          }}
          defaultValues={[ORDER_BY_OPTIONS.ContextTimestamp.filterName]}
          horizontalAlignment={ALIGNMENT.RIGHT}
          options={orderByOptions}
        />
      </div>
    </StyledMenu>
  );
};
